import {
    Cancel,
    CheckCircle,
    ContentCopy,
    ListAlt,
    Person,
    WarningRounded,
} from '@mui/icons-material';
import CallIcon from '@mui/icons-material/Call';
import MailIcon from '@mui/icons-material/MailOutline';
import { Avatar, Button, Chip, ChipProps, GridProps, Tooltip } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import moment from 'moment-timezone';
import React from 'react';
import { Link } from 'react-router';
import invariant from 'tiny-invariant';

import { DialogType, useDialog } from '../../dialogs/DialogManager';
import { ServiceBundleBasic } from '../../fetching/api/serviceBundles';
import { ServiceBasic } from '../../fetching/api/services';
import {
    QhpTeamBasic,
    ServiceAreaBasic,
    TerritorySimple,
} from '../../fetching/sharedSchemas/basicSchemas';
import {
    QualificationBasic,
    sortQualifications,
    useOrgPrefixedLabelForQualification,
} from '../../fetching/sharedSchemas/qualificationSchemas';
import { VisitPositionForList } from '../../fetching/sharedSchemas/visits';
import { ClickHandler, wrapOnClick } from '../../utils/clickEvent';
import {
    colorDateFromDayOfWeekStart,
    formatDatetimeRange,
    formatTimeRange,
} from '../../utils/datetimes/formatting';
import { TimeWindow } from '../../utils/datetimes/types';
import { formatInitials, formatPhoneNumberForDisplay } from '../../utils/formatting';
import { linkProps } from '../../utils/links';
import {
    Children,
    Component,
    DefiniteComponent,
    ReturnComponent,
    SxStyle,
} from '../../utils/reactTypes';
import { copyToClipboard } from '../buttons/CopyButton';
import { ClinicianChip } from '../clinicians/ClinicianChip';
import { ConditionalTooltip } from '../utils/conditionalComponents';
import { VerticalStack } from '../utils/linearLayouts';
import { NONE_TEXT } from './AxleInfoField';
import { CappedChipRows, useCappedList } from './expandableChips';

//////////////////////////////////////////////////////////////////////////////
// region Generic components.
//////////////////////////////////////////////////////////////////////////////

export interface IAxleChipProps extends ChipProps {
    // Left-hand icon.
    icon?: DefiniteComponent;
    avatar?: DefiniteComponent;
    numberIcon?: number;
    // Label and tooltip.
    label: string;
    tooltip?: string;
    // Clicking the chip.
    onClick?: ClickHandler;
    link?: string;
    routerLink?: string;
    // Right-hand action button.
    onAction?: ClickHandler;
    actionIcon?: DefiniteComponent;
    copyToClipboardValue?: string;
    // Hover over the chip
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
    // Styling.
    thin?: boolean;
    fullWidth?: boolean;
    sx?: SxStyle;
}

export const AxleChip = ({
    icon,
    avatar,
    numberIcon,
    label,
    tooltip = '',
    onClick,
    link,
    copyToClipboardValue,
    routerLink,
    onAction,
    actionIcon,
    onMouseEnter,
    onMouseLeave,
    thin = false,
    fullWidth = false,
    sx,
    ...rest
}: IAxleChipProps) => {
    const avatarProp = numberIcon ? <Avatar>{numberIcon}</Avatar> : avatar;

    const size = thin ? 'small' : 'medium';

    const linkProps_ = link
        ? {
              component: 'a',
              clickable: true,
              ...linkProps({ link }),
          }
        : routerLink
        ? {
              component: Link,
              to: routerLink,
              clickable: true,
          }
        : {};

    if (copyToClipboardValue) {
        invariant(onAction === undefined, 'Cannot have both copyToClipboardValue and onAction');
        invariant(actionIcon === undefined, 'Cannot have both copyToClipboardValue and actionIcon');
        onAction = (event) => {
            event.preventDefault();
            void copyToClipboard(copyToClipboardValue);
        };
        actionIcon = (
            <Tooltip title='Copy to clipboard'>
                <ContentCopy />
            </Tooltip>
        );
    }

    // Memoized so Emotion doesn't re-serialize chip styles on every render of
    // chip-heavy tables.
    const sxProp: SxStyle = React.useMemo(
        () => ({
            ...sx,
            ...(fullWidth && {
                width: '100%',
                justifyContent: 'space-between',
            }),
            // MUI makes the entire chip clickable/non-text-selectable if there is
            //  a delete/action icon, even if there is no link or click handler
            //  for the chip as a whole, so we have to undo that.
            ...(onClick === undefined &&
                routerLink === undefined &&
                link === undefined && {
                    cursor: 'default',
                    userSelect: 'inherit',
                }),
        }),
        [sx, fullWidth, onClick, routerLink, link],
    );

    return (
        <ConditionalTooltip title={tooltip} condition={!!tooltip}>
            <Chip
                {...rest}
                icon={icon}
                avatar={avatarProp}
                label={label}
                size={size}
                onClick={wrapOnClick(onClick)}
                deleteIcon={actionIcon}
                onDelete={wrapOnClick(onAction)}
                sx={sxProp}
                {...linkProps_}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            />
        </ConditionalTooltip>
    );
};

export const AxleChips = ({ labels }: { labels: string[] }): ReturnComponent => (
    <ChipsInRows>
        {labels.map((label, index) => {
            return <AxleChip key={index} label={label} tooltip={label} />;
        })}
    </ChipsInRows>
);

// A plain string-label chip list that caps at `previewCount` with a "Show all N"
// toggle beneath the chips (defaults to no cap). Used for simple lists like licenses.
export const CappedStringChips = ({
    labels,
    previewCount = Infinity,
}: {
    labels: string[];
    previewCount?: number;
}): ReturnComponent => {
    const { shown, total, canToggle, showAll, toggle } = useCappedList(labels, previewCount);
    return (
        <CappedChipRows canToggle={canToggle} showAll={showAll} total={total} onToggle={toggle}>
            <ChipsInRows>
                {shown.map((label) => (
                    <AxleChip key={label} label={label} />
                ))}
            </ChipsInRows>
        </CappedChipRows>
    );
};

export const ChipsInRows = ({
    children,
    ...gridProps
}: { children: Children } & GridProps): ReturnComponent => (
    <Grid container rowGap={1} columnGap={1} alignItems={'center'} {...gridProps}>
        {/*{ReactChildren.count(children) > 0 ? children : <>{NONE_TEXT}</>}*/}
        {children}
    </Grid>
);

export const ChipsVerticalStack = ({
    children,
    fullWidth = false,
}: {
    children: Children;
    fullWidth?: boolean;
}): ReturnComponent => (
    <VerticalStack spacing={0.5} fullWidth={fullWidth}>
        {children}
    </VerticalStack>
);

// A chip-sized button you can include in a row of chips, that will match the appearance of
//  a regular-sized (non-thin) chip.
export const ChipSizedButton = ({
    startIcon,
    label,
    onClick,
}: {
    startIcon: Component;
    label: string;
    onClick: () => void;
}): ReturnComponent => {
    return (
        <Button
            startIcon={startIcon}
            color={'secondary'}
            size={'small'}
            onClick={onClick}
            sx={{
                // Visually match the border radius of the chips.
                borderRadius: '15px',
            }}
        >
            {label}
        </Button>
    );
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Specialised components.
//////////////////////////////////////////////////////////////////////////////

export const PhoneChip = ({ phone }: { phone: string | null }): ReturnComponent => {
    if (!phone) {
        return <>{NONE_TEXT}</>;
    }
    const phoneNumberForDisplay: string = formatPhoneNumberForDisplay(phone);
    return (
        <AxleChip
            icon={<CallIcon />}
            label={phoneNumberForDisplay}
            copyToClipboardValue={phoneNumberForDisplay}
        />
    );
};

export const EmailChip = ({
    email,
    truncate = false,
}: {
    email: string | null;
    // Keep the email on one line and ellipsis the overflow (with the full address
    // in a tooltip) instead of letting it break mid-word. Opt-in for width-
    // constrained layouts like the clinician header grid.
    truncate?: boolean;
}): ReturnComponent => {
    if (!email) {
        return <>{NONE_TEXT}</>;
    }
    return (
        <AxleChip
            icon={<MailIcon />}
            label={email}
            link={`mailto:${email}`}
            copyToClipboardValue={email}
            tooltip={truncate ? email : ''}
            sx={
                truncate
                    ? {
                          // `&&` outweighs an ancestor `.MuiChip-label` wrap rule so the
                          // address truncates instead of breaking across lines.
                          '&& .MuiChip-label': {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                          },
                      }
                    : undefined
            }
        />
    );
};

export const QualificationChips = ({
    qualifications,
    previewCount = Infinity,
}: {
    qualifications: QualificationBasic[];
    // Cap the list and reveal the rest via a "Show all N" toggle. Defaults to no
    // cap so existing callers are unaffected.
    previewCount?: number;
}): ReturnComponent => {
    const qualificationLabel = useOrgPrefixedLabelForQualification(qualifications);
    const sorted = sortQualifications(qualifications);
    const { shown, total, canToggle, showAll, toggle } = useCappedList(sorted, previewCount);

    if (total === 0) {
        return <>{NONE_TEXT}</>;
    }

    return (
        <CappedChipRows canToggle={canToggle} showAll={showAll} total={total} onToggle={toggle}>
            <ChipsInRows>
                {shown.map((qualification) => {
                    return (
                        <AxleChip
                            // Key by the stable uuid, not code/name: org-scoped labels can
                            // collide (two orgs' "AETNA (PPO)"), which would produce duplicate
                            // React keys and reconciliation glitches.
                            key={qualification.uuid}
                            label={qualificationLabel(qualification)}
                            tooltip={qualification.description}
                        />
                    );
                })}
            </ChipsInRows>
        </CappedChipRows>
    );
};

type QualificationBasicWithOrg = {
    id: string;
    name: string;
    description: string;
    organization: {
        id: string;
        name: string;
    };
};

export const QualificationChipsGql = ({
    qualifications,
    previewCount,
}: {
    qualifications: QualificationBasicWithOrg[];
    previewCount?: number;
}): ReturnComponent => {
    return (
        <QualificationChips
            previewCount={previewCount}
            qualifications={qualifications.map((qual) => ({
                uuid: qual.id,
                code: qual.name,
                description: qual.description,
                organization: {
                    uuid: qual.organization.id,
                    name: qual.organization.name,
                },
            }))}
        />
    );
};

export const IdentityChip = ({
    logoUrl,
    firstName,
    lastName,
    link,
    color,
    wrap = false,
}: {
    logoUrl?: string;
    firstName: string;
    lastName: string;
    link?: string;
    color?: string;
    wrap?: boolean;
}) => {
    return (
        <AxleChip
            avatar={
                <Avatar src={logoUrl} alt={`${firstName} ${lastName}`.trim()}>
                    {formatInitials(firstName, lastName)}
                </Avatar>
            }
            label={`${firstName} ${lastName}`.trim()}
            link={link}
            sx={{
                backgroundColor: color ? 'FFFFFF' : undefined,
                color: color,
                border: color ? `2px solid ${color}` : undefined,
                fontWeight: color ? '500' : undefined,
                ...(wrap && {
                    // If the label wraps across multiple lines, make sure all lines get fitted.
                    height: '100%',
                    // We need this, otherwise if the label is all on one line, the chip is too thin,
                    //  and looks weird in comparison to other chips.
                    minHeight: '32px',
                    '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        lineHeight: '1.25em',
                    },
                }),
            }}
        />
    );
};

export type QhpChipProps = {
    qhp: {
        uuid: string;
        name: string;
        first_name: string;
        last_name: string;
        logo_url: string | null;
    } | null;
    showLink: boolean;
    wrap?: boolean;
    services?: ServiceBasic[];
    showServices?: boolean;
    color?: string;
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
};

export const QhpChip = ({
    qhp,
    showLink,
    wrap = false,
    services = [],
    showServices = false,
    color,
    onMouseEnter,
    onMouseLeave,
}: QhpChipProps): ReturnComponent => {
    const serviceLabel =
        showServices && services.length > 0
            ? ' (' + [...new Set(services.map((service) => service.name))].join(', ') + ')'
            : '';

    if (!qhp) {
        return <AxleChip icon={<WarningRounded />} label={NONE_TEXT + serviceLabel} />;
    }

    return (
        <AxleChip
            avatar={
                <Avatar
                    src={qhp.logo_url ?? undefined}
                    alt={`${qhp.first_name} ${qhp.last_name}`.trim() || qhp.name}
                >
                    {formatInitials(qhp.first_name, qhp.last_name)}
                </Avatar>
            }
            label={[qhp.first_name, qhp.last_name, serviceLabel].filter(Boolean).join(' ')}
            link={showLink ? `/clinicians/${qhp.uuid}` : undefined}
            sx={{
                backgroundColor: color ? 'FFFFFF' : undefined,
                color: color,
                border: color ? `2px solid ${color}` : undefined,
                fontWeight: color ? '500' : undefined,
                ...(wrap && {
                    // If the label wraps across multiple lines, make sure all lines get fitted.
                    height: '100%',
                    // We need this, otherwise if the label is all on one line, the chip is too thin,
                    //  and looks weird in comparison to other chips.
                    minHeight: '32px',
                    '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        lineHeight: '1.25em',
                    },
                }),
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        />
    );
};

export const ClinicianTeamsChips = ({
    teams,
    isEdit,
    layout = 'vertical',
}: {
    teams: {
        id: string;
        name: string;
    }[];
    isEdit: boolean;
    // Default stays vertical to preserve every existing caller; the clinician header
    // opts into 'horizontal' so multiple teams flow in a wrapping row beside the name.
    // 'inline' renders the chips with NO wrapper element (a bare fragment) so the
    // caller can place them as direct flex siblings of a label/toggle — used by the
    // header's identity band so expanded teams flow inline instead of as a block.
    layout?: 'vertical' | 'horizontal' | 'inline';
}) => {
    const { openDialog } = useDialog();

    // Memoized so the chips' onClick identity is stable across renders.
    const handleChipClick = React.useCallback(
        (clinicianTeamId: string) =>
            isEdit
                ? openDialog(DialogType.EditClinicianTeam, { clinicianTeamId })
                : openDialog(DialogType.ClinicianTeamDetails, { clinicianTeamId }),
        [isEdit, openDialog],
    );

    if (teams.length === 0) {
        return <>{NONE_TEXT}</>;
    }

    const chips = teams.map((team) => (
        <AxleChip
            // Key by the stable team id, not the array index, so reorders/edits don't
            // remount the wrong chip.
            key={team.id}
            label={team.name}
            // Full name on hover so a pill truncated by an ellipsis (e.g. the header's
            // capped-width team pills) is still readable.
            tooltip={team.name}
            onClick={() => handleChipClick(team.id)}
        />
    ));

    if (layout === 'inline') {
        return <>{chips}</>;
    }

    const Wrapper = layout === 'horizontal' ? ChipsInRows : ChipsVerticalStack;
    return <Wrapper>{chips}</Wrapper>;
};

export const QhpTeamsChips = ({
    teams,
    isEdit,
}: {
    teams: QhpTeamBasic[];
    isEdit: boolean;
}): ReturnComponent => {
    const { openDialog } = useDialog();

    const handleChipClick = (qhp_team_uuid: string) => {
        return isEdit
            ? openDialog(DialogType.EditClinicianTeam, { clinicianTeamId: qhp_team_uuid })
            : openDialog(DialogType.ClinicianTeamDetails, { clinicianTeamId: qhp_team_uuid });
    };

    if (teams.length === 0) {
        return <>{NONE_TEXT}</>;
    }

    return (
        <>
            <ChipsVerticalStack>
                {teams.map((team, index) => {
                    return (
                        <AxleChip
                            key={index}
                            label={team.name}
                            onClick={() => handleChipClick(team.uuid)}
                        />
                    );
                })}
            </ChipsVerticalStack>
        </>
    );
};

export const ClinicianAppointmentAssigneeChips = ({
    clinicianAppointments,
    showUnassignedSlots,
    showLink,
    showServices,
}: {
    clinicianAppointments: {
        id: string;
        clinician: {
            id: string;
            firstName: string;
            lastName: string;
            logoUrl: string | null;
        } | null;
        encounters: {
            id: string;
            service: {
                id: string;
                name: string;
            };
        }[];
    }[];
    showUnassignedSlots: boolean;
    showLink: boolean;
    showServices: boolean;
}): ReturnComponent => {
    if (clinicianAppointments.length === 0) {
        return <>{NONE_TEXT}</>;
    }
    return (
        <ChipsVerticalStack>
            {clinicianAppointments
                .filter((appointment) => showUnassignedSlots || appointment.clinician)
                .map((appointment) => (
                    <ClinicianChip
                        key={appointment.id}
                        clinician={appointment.clinician}
                        showLink={showLink}
                        services={appointment.encounters.map((encounter) => encounter.service)}
                        showServices={showServices}
                        wrap={true}
                    />
                ))}
        </ChipsVerticalStack>
    );
};

export const AssigneeChips = ({
    visit_positions,
    showUnassignedSlots,
    showLink,
    showServices,
}: {
    visit_positions: VisitPositionForList[];
    showUnassignedSlots: boolean;
    showLink: boolean;
    showServices: boolean;
}): ReturnComponent => {
    if (visit_positions.length === 0) {
        return <>{NONE_TEXT}</>;
    }
    return (
        <ChipsVerticalStack>
            {visit_positions
                .filter((visit_position) => !!visit_position.qhp || showUnassignedSlots)
                .map((visitPosition, idx) => (
                    <QhpChip
                        key={idx}
                        qhp={visitPosition.qhp}
                        showLink={showLink}
                        services={visitPosition.visit_services.map(
                            (visitService) => visitService.service,
                        )}
                        showServices={showServices}
                        wrap={true}
                    />
                ))}
        </ChipsVerticalStack>
    );
};

export const AppointmentEncounterChips = ({
    encounters,
    wrap = false,
}: {
    encounters: {
        id: string;
        isCompleted: boolean | null;
        service: {
            id: string;
            name: string;
            isActive?: boolean;
        };
    }[];
    wrap?: boolean;
}): ReturnComponent => (
    <ChipsVerticalStack>
        {encounters.map((encounter) => {
            const icon =
                encounter.isCompleted === null ? undefined : encounter.isCompleted ? (
                    <CheckCircle color='primary' />
                ) : (
                    <Cancel color='error' />
                );
            return (
                <AxleChip
                    key={encounter.id}
                    icon={icon}
                    label={
                        encounter.service.isActive === false
                            ? `${encounter.service.name} (Inactive)`
                            : encounter.service.name
                    }
                    thin={true}
                    sx={{
                        padding: '0.2em',
                        ...(wrap && {
                            // If the label wraps across multiple lines, make sure all lines get fitted.
                            height: '100%',
                            // We need this, otherwise if the label is all on one line, the chip is too thin,
                            //  and looks weird in comparison to other chips.
                            minHeight: '32px',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                textAlign: 'left',
                                lineHeight: '1.25em',
                            },
                        }),
                    }}
                />
            );
        })}
    </ChipsVerticalStack>
);

export const VisitEncounterChips = ({
    encounters,
    wrap = false,
}: {
    encounters: {
        id: string;
        isCompleted: boolean | null;
        service: {
            id: string;
            name: string;
        };
    }[];
    wrap?: boolean;
}): ReturnComponent => {
    return (
        <ChipsVerticalStack>
            {encounters.map((encounter) => {
                const icon =
                    encounter.isCompleted === null ? undefined : encounter.isCompleted ? (
                        <CheckCircle color='primary' />
                    ) : (
                        <Cancel color='error' />
                    );
                return (
                    <AxleChip
                        key={encounter.id}
                        icon={icon}
                        label={encounter.service.name}
                        thin={true}
                        sx={{
                            padding: '0.2em',
                            ...(wrap && {
                                // If the label wraps across multiple lines, make sure all lines get fitted.
                                height: '100%',
                                // We need this, otherwise if the label is all on one line, the chip is too thin,
                                //  and looks weird in comparison to other chips.
                                minHeight: '32px',
                                '& .MuiChip-label': {
                                    whiteSpace: 'normal',
                                    textAlign: 'left',
                                    lineHeight: '1.25em',
                                },
                            }),
                        }}
                    />
                );
            })}
        </ChipsVerticalStack>
    );
};

export const VisitServiceChips = ({
    visitServices,
    wrap = false,
}: {
    visitServices: {
        is_completed: boolean | null;
        service: { name: string };
    }[];
    wrap?: boolean;
}): ReturnComponent => (
    <ChipsVerticalStack>
        {visitServices.map((visitService, idx) => {
            const icon =
                visitService.is_completed === null ? undefined : visitService.is_completed ? (
                    <CheckCircle color='primary' />
                ) : (
                    <Cancel color='error' />
                );
            return (
                <AxleChip
                    key={idx}
                    icon={icon}
                    label={visitService.service.name}
                    thin={true}
                    sx={{
                        padding: '0.2em',
                        ...(wrap && {
                            // If the label wraps across multiple lines, make sure all lines get fitted.
                            height: '100%',
                            // We need this, otherwise if the label is all on one line, the chip is too thin,
                            //  and looks weird in comparison to other chips.
                            minHeight: '32px',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                textAlign: 'left',
                                lineHeight: '1.25em',
                            },
                        }),
                    }}
                />
            );
        })}
    </ChipsVerticalStack>
);

export const ServiceChips = ({ services }: { services: { name: string }[] }): ReturnComponent => {
    return (
        <VisitServiceChips
            visitServices={services.map((service) => ({
                service: service,
                is_completed: null,
            }))}
            wrap={true}
        />
    );
};

const areAllWindowsOnSameDate = (windows: TimeWindow[]): boolean => {
    const earliestStart = moment.min(windows.map((window) => window.start));
    const lastEnd = moment.max(windows.map((window) => window.end));
    return earliestStart.isSame(lastEnd, 'date');
};

export const TimeWindowsChips = ({
    timeWindows,
    timezone,
    onDelete,
    hideDateIfSameDate = false,
    colorFromDayOfWeek,
}: {
    timeWindows: TimeWindow[];
    timezone: string | null;
    onDelete?: (timeWindow: TimeWindow) => void;
    hideDateIfSameDate?: boolean;
    colorFromDayOfWeek?: number;
}): ReturnComponent => {
    const showOnlyTime = hideDateIfSameDate && areAllWindowsOnSameDate(timeWindows);
    return (
        <ChipsInRows>
            {timeWindows
                .sort((window1, window2) => window1.start.diff(window2.start))
                .map((timeWindow, idx) => (
                    <AxleChip
                        key={idx}
                        label={(showOnlyTime ? formatTimeRange : formatDatetimeRange)(
                            timeWindow.start,
                            timeWindow.end,
                            timezone,
                        )}
                        thin={true}
                        onAction={onDelete ? () => onDelete(timeWindow) : undefined}
                        sx={
                            colorFromDayOfWeek !== undefined
                                ? {
                                      backgroundColor: colorDateFromDayOfWeekStart(
                                          timeWindow.start,
                                          colorFromDayOfWeek,
                                      ),
                                  }
                                : {}
                        }
                    />
                ))}
        </ChipsInRows>
    );
};

export const PatientChipGql = ({
    patient: { id, fullName },
    showLink,
}: {
    patient: { id: string; fullName: string };
    showLink: boolean;
}): ReturnComponent => (
    <AxleChip
        icon={<Person />}
        label={fullName}
        routerLink={showLink ? `/patients/${id}` : undefined}
        copyToClipboardValue={fullName}
    />
);

export const PatientChip = ({
    patient,
    showLink,
}: {
    patient: { uuid: string; name: string };
    showLink: boolean;
}): ReturnComponent => (
    <AxleChip
        icon={<Person />}
        label={patient.name}
        link={showLink ? `/patients/${patient.uuid}` : undefined}
        copyToClipboardValue={patient.name}
    />
);

export const UUIDChip = ({ uuid }: { uuid: string }): ReturnComponent => (
    <AxleChip label={uuid} copyToClipboardValue={uuid} />
);

export const VisitOrderChip = ({
    visitOrder,
    showLink,
}: {
    visitOrder: { id: string };
    showLink: boolean;
}): ReturnComponent => (
    <AxleChip
        icon={<ListAlt />}
        label={'View Details'}
        link={showLink ? `/visit-orders/${visitOrder.id}` : undefined}
    />
);

export const ServiceAreaChip = ({
    service_area,
}: {
    service_area: ServiceAreaBasic;
}): ReturnComponent => (
    <AxleChip label={service_area.name} link={`/service-areas/${service_area.uuid}`} />
);

export const ServiceAreaChipGql = ({
    serviceArea,
}: {
    serviceArea: { name: string; id: string };
}): ReturnComponent => (
    <AxleChip label={serviceArea.name} link={`/service-areas/${serviceArea.id}`} />
);

export const ServiceChip = ({
    service,
    showLink,
}: {
    service: ServiceBasic;
    showLink: boolean;
}): ReturnComponent => (
    <AxleChip
        label={service.name}
        link={showLink ? `/services/` : undefined}
        color={service.is_active ? undefined : 'error'}
        variant={service.is_active ? undefined : 'outlined'}
    />
);

export const ServiceChipGql = ({
    service,
    showLink,
    ...rest
}: {
    service: {
        name: string;
    };
    showLink: boolean;
} & Omit<IAxleChipProps, 'label'>): ReturnComponent => (
    <AxleChip label={service.name} link={showLink ? `/services/` : undefined} {...rest} />
);

export const ServiceBundleChip = ({
    serviceBundle,
    showLink,
}: {
    serviceBundle: ServiceBundleBasic;
    showLink: boolean;
}): ReturnComponent => (
    <AxleChip label={serviceBundle.name} link={showLink ? `/service-bundles/` : undefined} />
);

export const ScrollableChips = ({ children }: { children: Children }): ReturnComponent => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            overflow: 'hidden',
            overflowY: 'auto',
            maxWidth: '25em',
            maxHeight: '10em',
            gap: '0.5rem',
        }}
    >
        {children}
    </div>
);

export const TerritoryChip = ({
    territory,
    showLink,
}: {
    territory: TerritorySimple;
    showLink: boolean;
}): ReturnComponent => (
    <AxleChip label={territory.name} link={showLink ? `/territories/` : undefined} />
);

export const TerritoryChips = ({
    territories,
    showLink,
}: {
    territories: { name: string; id: string }[];
    showLink: boolean;
}): ReturnComponent => {
    if (territories.length === 0) {
        return <>{NONE_TEXT}</>;
    }
    return (
        <ChipsInRows>
            {territories.map((territory) => (
                <AxleChip
                    key={territory.id}
                    label={territory.name}
                    link={showLink ? `/territories/` : undefined}
                />
            ))}
        </ChipsInRows>
    );
};

// endregion
